import { useEffect, useRef, useState } from 'react';

const APP_LOADED_VERSION_KEY = 'app-loaded-version';
const PROD_POLL_INTERVAL_MS = 5 * 60 * 1000;
const DEV_POLL_INTERVAL_MS = 30 * 1000;

type VersionPayload = {
  version: string;
};

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const versionUrl = `${import.meta.env.BASE_URL}version.json`;
    const response = await fetch(`${versionUrl}?t=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) return null;

    const payload = (await response.json()) as VersionPayload;
    return payload.version ?? null;
  } catch {
    return null;
  }
}

function getLoadedVersion(): string | null {
  return import.meta.env.VITE_APP_BUILD_ID ?? sessionStorage.getItem(APP_LOADED_VERSION_KEY);
}

export function useAppVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const hasNotifiedRef = useRef(false);
  const pollIntervalMs = import.meta.env.DEV ? DEV_POLL_INTERVAL_MS : PROD_POLL_INTERVAL_MS;

  useEffect(() => {
    const checkForUpdate = async () => {
      if (hasNotifiedRef.current) return;

      const remoteVersion = await fetchRemoteVersion();
      if (!remoteVersion) return;

      const loadedVersion = getLoadedVersion();

      if (!loadedVersion) {
        sessionStorage.setItem(APP_LOADED_VERSION_KEY, remoteVersion);
        return;
      }

      if (remoteVersion === loadedVersion) return;

      hasNotifiedRef.current = true;
      setUpdateAvailable(true);
    };

    void checkForUpdate();

    const intervalId = window.setInterval(() => {
      void checkForUpdate();
    }, pollIntervalMs);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pollIntervalMs]);

  return { updateAvailable };
}

export function reloadAppForUpdate() {
  sessionStorage.removeItem(APP_LOADED_VERSION_KEY);
  window.location.reload();
}
